import * as z from 'zod';

export const GetAttendanceRequest = z.object({
  attendanceId: z
    .string()
    .describe('ID of the attendance object to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetAttendanceResponse = z.object({
  _id: z
    .string()
    .describe('ID of the `attendance` object.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  bookingId: z
    .string()
    .describe('Corresponding booking ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  sessionId: z
    .string()
    .describe(
      'Corresponding session ID.\n\n*Deprecated.** Use `eventId` instead.'
    )
    .optional()
    .nullable(),
  status: z
    .enum(['NOT_SET', 'ATTENDED', 'NOT_ATTENDED'])
    .describe('Status indicating if any participants attended the session.')
    .optional(),
  numberOfAttendees: z
    .number()
    .int()
    .describe(
      'Total number of participants that attended the session. By default, the number\nof attendees is set to `1`, but you can set a number to greater than `1` if multiple\nparticipants attended.\n\nDo not set to `0` to indicate that no one attended the session. Instead, set the `status` field to `NOT_ATTENDED`.\n\nDefault: 1'
    )
    .optional(),
  eventId: z
    .string()
    .describe(
      "Corresponding [event ID](https://dev.wix.com/docs/api-reference/business-management/calendar/events-v3/event-object). Use this field instead of the deprecated `sessionId`.\nYou can retrieve the event ID from the booking's slot `booking.bookedEntity.slot.eventId`."
    )
    .min(36)
    .max(250)
    .optional()
    .nullable(),
});
export const SetAttendanceRequest = z.object({
  attendance: z
    .object({
      _id: z
        .string()
        .describe('ID of the `attendance` object.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      bookingId: z
        .string()
        .describe('Corresponding booking ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        ),
      sessionId: z
        .string()
        .describe(
          'Corresponding session ID.\n\n*Deprecated.** Use `eventId` instead.'
        )
        .optional()
        .nullable(),
      status: z.enum(['NOT_SET', 'ATTENDED', 'NOT_ATTENDED']),
      numberOfAttendees: z
        .number()
        .int()
        .describe(
          'Total number of participants that attended the session. By default, the number\nof attendees is set to `1`, but you can set a number to greater than `1` if multiple\nparticipants attended.\n\nDo not set to `0` to indicate that no one attended the session. Instead, set the `status` field to `NOT_ATTENDED`.\n\nDefault: 1'
        )
        .optional(),
      eventId: z
        .string()
        .describe(
          "Corresponding [event ID](https://dev.wix.com/docs/api-reference/business-management/calendar/events-v3/event-object). Use this field instead of the deprecated `sessionId`.\nYou can retrieve the event ID from the booking's slot `booking.bookedEntity.slot.eventId`."
        )
        .min(36)
        .max(250)
        .optional()
        .nullable(),
    })
    .describe('Attendance to create or update.'),
  options: z
    .object({
      participantNotification: z
        .object({
          notifyParticipants: z
            .boolean()
            .describe(
              'Specify whether to send a message about the changes to the customer.\n\nDefault: `false`'
            )
            .optional()
            .nullable(),
          message: z
            .string()
            .describe(
              'Optional custom message to send to the participants about the changes to the booking.'
            )
            .min(1)
            .max(5000)
            .optional()
            .nullable(),
        })
        .describe(
          'Information about whether to send a message to a customer after their attendance was set.'
        )
        .optional(),
    })
    .describe('Options to use when setting an attendance.')
    .optional(),
});
export const SetAttendanceResponse = z.object({
  attendance: z
    .object({
      _id: z
        .string()
        .describe('ID of the `attendance` object.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      bookingId: z
        .string()
        .describe('Corresponding booking ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      sessionId: z
        .string()
        .describe(
          'Corresponding session ID.\n\n*Deprecated.** Use `eventId` instead.'
        )
        .optional()
        .nullable(),
      status: z
        .enum(['NOT_SET', 'ATTENDED', 'NOT_ATTENDED'])
        .describe('Status indicating if any participants attended the session.')
        .optional(),
      numberOfAttendees: z
        .number()
        .int()
        .describe(
          'Total number of participants that attended the session. By default, the number\nof attendees is set to `1`, but you can set a number to greater than `1` if multiple\nparticipants attended.\n\nDo not set to `0` to indicate that no one attended the session. Instead, set the `status` field to `NOT_ATTENDED`.\n\nDefault: 1'
        )
        .optional(),
      eventId: z
        .string()
        .describe(
          "Corresponding [event ID](https://dev.wix.com/docs/api-reference/business-management/calendar/events-v3/event-object). Use this field instead of the deprecated `sessionId`.\nYou can retrieve the event ID from the booking's slot `booking.bookedEntity.slot.eventId`."
        )
        .min(36)
        .max(250)
        .optional()
        .nullable(),
    })
    .describe('Created or updated attendance.')
    .optional(),
});
export const BulkSetAttendanceRequest = z.object({
  options: z
    .object({
      returnFullEntity: z.boolean().optional(),
      attendanceDetails: z
        .array(
          z.object({
            attendance: z
              .object({
                _id: z
                  .string()
                  .describe('ID of the `attendance` object.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                bookingId: z
                  .string()
                  .describe('Corresponding booking ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                sessionId: z
                  .string()
                  .describe(
                    'Corresponding session ID.\n\n*Deprecated.** Use `eventId` instead.'
                  )
                  .optional()
                  .nullable(),
                status: z
                  .enum(['NOT_SET', 'ATTENDED', 'NOT_ATTENDED'])
                  .optional(),
                numberOfAttendees: z
                  .number()
                  .int()
                  .describe(
                    'Total number of participants that attended the session. By default, the number\nof attendees is set to `1`, but you can set a number to greater than `1` if multiple\nparticipants attended.\n\nDo not set to `0` to indicate that no one attended the session. Instead, set the `status` field to `NOT_ATTENDED`.\n\nDefault: 1'
                  )
                  .optional(),
                eventId: z
                  .string()
                  .describe(
                    "Corresponding [event ID](https://dev.wix.com/docs/api-reference/business-management/calendar/events-v3/event-object). Use this field instead of the deprecated `sessionId`.\nYou can retrieve the event ID from the booking's slot `booking.bookedEntity.slot.eventId`."
                  )
                  .min(36)
                  .max(250)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Created or updated attendance information for a booking session.'
              )
              .optional(),
            participantNotification: z
              .object({
                notifyParticipants: z
                  .boolean()
                  .describe(
                    'Specify whether to send a message about the changes to the customer.\n\nDefault: `false`'
                  )
                  .optional()
                  .nullable(),
                message: z
                  .string()
                  .describe(
                    'Optional custom message to send to the participants about the changes to the booking.'
                  )
                  .min(1)
                  .max(5000)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Information about whether to send a message to the customer after their attendance was set.'
              )
              .optional(),
          })
        )
        .max(8)
        .optional(),
    })
    .describe('Options to use when setting multiple attendances in bulk.')
    .optional(),
});
export const BulkSetAttendanceResponse = z.object({
  results: z
    .array(
      z.object({
        item: z
          .object({
            _id: z
              .string()
              .describe('ID of the `attendance` object.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            bookingId: z
              .string()
              .describe('Corresponding booking ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            sessionId: z
              .string()
              .describe(
                'Corresponding session ID.\n\n*Deprecated.** Use `eventId` instead.'
              )
              .optional()
              .nullable(),
            status: z
              .enum(['NOT_SET', 'ATTENDED', 'NOT_ATTENDED'])
              .describe(
                'Status indicating if any participants attended the session.'
              )
              .optional(),
            numberOfAttendees: z
              .number()
              .int()
              .describe(
                'Total number of participants that attended the session. By default, the number\nof attendees is set to `1`, but you can set a number to greater than `1` if multiple\nparticipants attended.\n\nDo not set to `0` to indicate that no one attended the session. Instead, set the `status` field to `NOT_ATTENDED`.\n\nDefault: 1'
              )
              .optional(),
            eventId: z
              .string()
              .describe(
                "Corresponding [event ID](https://dev.wix.com/docs/api-reference/business-management/calendar/events-v3/event-object). Use this field instead of the deprecated `sessionId`.\nYou can retrieve the event ID from the booking's slot `booking.bookedEntity.slot.eventId`."
              )
              .min(36)
              .max(250)
              .optional()
              .nullable(),
          })
          .describe('Created or updated `attendance` object.')
          .optional(),
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for the created or updated `attendance` object.')
          .optional(),
      })
    )
    .min(1)
    .max(8)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe(
      'Information about the total number of successes and failures for the Bulk Set Attendance call.'
    )
    .optional(),
});
export const QueryAttendanceRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          bookingId: z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          sessionId: z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          eventId: z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          status: z
            .object({
              $eq: z.string(),
              $in: z.array(z.string()),
              $ne: z.string(),
              $nin: z.array(z.string()),
            })
            .partial()
            .strict()
            .optional(),
          numberOfAttendees: z
            .object({
              $eq: z.number(),
              $gt: z.number(),
              $gte: z.number(),
              $in: z.array(z.number()),
              $lt: z.number(),
              $lte: z.number(),
              $ne: z.number(),
              $nin: z.array(z.number()),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum([
                '_id',
                'bookingId',
                'sessionId',
                'eventId',
                'status',
                'numberOfAttendees',
              ])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('Query options.'),
});
export const QueryAttendanceResponse = z.object({
  attendances: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('ID of the `attendance` object.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        bookingId: z
          .string()
          .describe('Corresponding booking ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        sessionId: z
          .string()
          .describe(
            'Corresponding session ID.\n\n*Deprecated.** Use `eventId` instead.'
          )
          .optional()
          .nullable(),
        status: z
          .enum(['NOT_SET', 'ATTENDED', 'NOT_ATTENDED'])
          .describe(
            'Status indicating if any participants attended the session.'
          )
          .optional(),
        numberOfAttendees: z
          .number()
          .int()
          .describe(
            'Total number of participants that attended the session. By default, the number\nof attendees is set to `1`, but you can set a number to greater than `1` if multiple\nparticipants attended.\n\nDo not set to `0` to indicate that no one attended the session. Instead, set the `status` field to `NOT_ATTENDED`.\n\nDefault: 1'
          )
          .optional(),
        eventId: z
          .string()
          .describe(
            "Corresponding [event ID](https://dev.wix.com/docs/api-reference/business-management/calendar/events-v3/event-object). Use this field instead of the deprecated `sessionId`.\nYou can retrieve the event ID from the booking's slot `booking.bookedEntity.slot.eventId`."
          )
          .min(36)
          .max(250)
          .optional()
          .nullable(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to previous page in the list of results.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Use these cursors to paginate between results. [Read more](https://dev.wix.com/api/rest/getting-started/api-query-language#getting-started_api-query-language_cursor-paging).'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Indicates if there are more results after the current page.\nIf `true`, another page of results can be retrieved.\nIf `false`, this is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Metadata for the paged set of results.')
    .optional(),
});
export const CountAttendancesRequest = z.object({
  options: z
    .object({
      filter: z
        .record(z.string(), z.any())
        .describe(
          'Filter criteria for counting attendance records. If not provided, counts all attendance records for the contact.'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const CountAttendancesResponse = z.object({
  count: z
    .number()
    .int()
    .describe('Total number of attendance records matching the filters.')
    .optional(),
});
export const DeleteAttendanceRequest = z.object({
  attendanceId: z
    .string()
    .describe('ID of the attendance record to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteAttendanceResponse = z.object({});
export const BulkDeleteAttendancesRequest = z.object({
  attendanceIds: z.array(z.string()).min(1).max(8),
});
export const BulkDeleteAttendancesResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for the deleted attendance.')
          .optional(),
      })
    )
    .min(1)
    .max(8)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe(
      'Information about the total number of successes and failures for the Bulk Delete Attendances call.'
    )
    .optional(),
});
